import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Receipt, ExternalLink, Pencil, Check, ChevronDown, ChevronUp } from "lucide-react";

// V1.55 · Módulo 6 — Ficha de facturación con copia/pega rápido.
// Facilita emitir la factura: datos del alumno listos para pegar y el
// precio final (IVA y descuento ya aplicados) del último contrato.

const fmtEur = (v) =>
  Number(v || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });

const buildAddress = (s) => {
  const via = [s.tipo_via, s.nombre_via, s.numero_via].filter(Boolean).join(" ");
  const extras = [
    s.bloque && `Bloque ${s.bloque}`,
    s.portal && `Portal ${s.portal}`,
    s.escalera && `Esc. ${s.escalera}`,
    s.planta && `${s.planta}º`,
    s.puerta && s.puerta,
  ].filter(Boolean).join(", ");
  const loc = [s.codigo_postal, s.localidad, s.provincia].filter(Boolean).join(" ");
  return [via, extras, loc].filter(Boolean).join(", ");
};

function CopyRow({ label, value, testId }) {
  const copy = async () => {
    if (!value) { toast.error("Dato no disponible en la ficha"); return; }
    await navigator.clipboard.writeText(String(value));
    toast.success(`${label} copiado`);
  };
  return (
    <div className="flex items-center gap-2 py-1 border-b border-neutral-100 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase text-neutral-400 font-semibold">{label}</div>
        <div className={"text-sm truncate " + (value ? "font-semibold text-neutral-800" : "italic text-neutral-400")}>
          {value || "—"}
        </div>
      </div>
      <button type="button" onClick={copy} data-testid={testId}
        className="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-semibold bg-neutral-100 hover:bg-orange-100 hover:text-orange-800 text-neutral-700 transition-colors">
        <Copy className="h-3.5 w-3.5" /> Copiar
      </button>
    </div>
  );
}

export default function InvoiceCopyPanel({ student, contracts = [], defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/settings/billing_invoice_url");
        setInvoiceUrl(data?.value || "");
      } catch { /* silencioso */ }
    })();
  }, [open]);

  const saveUrl = async () => {
    try {
      await api.patch("/settings/billing_invoice_url", { value: urlDraft.trim() });
      setInvoiceUrl(urlDraft.trim());
      setEditingUrl(false);
      toast.success("Enlace de facturación guardado");
    } catch {
      toast.error("No se pudo guardar el enlace");
    }
  };

  const fullName = [student?.name, student?.primer_apellido, student?.segundo_apellido]
    .filter(Boolean).join(" ");
  const address = student ? buildAddress(student) : "";
  const latest = (contracts || []).filter((c) => (c.document_kind || "contract") === "contract")[0];
  const priceLine = latest
    ? `${latest.pack_name || "Contrato"} …. ${fmtEur(latest.precio_final || latest.total)}`
    : "";

  return (
    <div className="card-surface" data-testid="invoice-copy-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="invoice-panel-toggle"
        className="w-full flex items-center justify-between p-3 text-sm font-semibold text-neutral-800">
        <span className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-emerald-600" /> Ficha de facturación (copia/pega)
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-1">
          <CopyRow label="Nombre y apellidos" value={fullName} testId="invoice-copy-name" />
          <CopyRow label="DNI" value={student?.dni} testId="invoice-copy-dni" />
          <CopyRow label="Dirección completa" value={address} testId="invoice-copy-address" />
          <CopyRow label="Código postal" value={student?.codigo_postal} testId="invoice-copy-cp" />
          <CopyRow label="Concepto y precio (IVA y dto. incluidos)" value={priceLine} testId="invoice-copy-price" />

          <div className="flex items-center gap-2 pt-2">
            {editingUrl ? (
              <>
                <input type="url" value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)}
                  placeholder="https://… (URL del emisor de facturas)" data-testid="invoice-url-input"
                  className="flex-1 rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <button type="button" onClick={saveUrl} data-testid="invoice-url-save"
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Check className="h-3.5 w-3.5" /> Guardar
                </button>
              </>
            ) : (
              <>
                <a href={invoiceUrl || undefined} target="_blank" rel="noreferrer" data-testid="invoice-emit-link"
                  onClick={(e) => { if (!invoiceUrl) { e.preventDefault(); toast.info("Configura primero el enlace con el lápiz"); } }}
                  className={"flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-lg text-sm font-semibold transition-colors " +
                    (invoiceUrl ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-neutral-100 text-neutral-400 cursor-not-allowed")}>
                  <ExternalLink className="h-4 w-4" /> Emitir factura
                </a>
                <button type="button" onClick={() => { setUrlDraft(invoiceUrl); setEditingUrl(true); }}
                  data-testid="invoice-url-edit" title="Configurar enlace del emisor de facturas"
                  className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-600">
                  <Pencil className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
